import capitalize from 'lodash/capitalize';
import React, { ForwardedRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useEntitiesWithReactQuery } from '~/hooks/use-entities';
import { useThemeExposure } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { useKnowledgeGraphDropdownItems } from '~/hooks/use-knowledge-graph-dropdown-items/useKnowledgeGraphDropdownItems';
import { useScreenerDropdownItems } from '~/hooks/use-screener-dropdown-items/useScreenerDropdownItems';
import { httpCode } from '~/utils/api-fetch/httpCode';

import { KnowledgeGraphModule } from '../knowledge-graph-module/KnowledgeGraphModule';
import * as S from './ThemeExposureModule.styles';

export interface ThemeExposureModuleProps {
  entityTag: string;
  isEtf: boolean;
}

export const ThemeExposureModule = forwardRef(
  (
    { entityTag, isEtf }: ThemeExposureModuleProps,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const { t } = useTranslation('widget');
    const { goToThemeOverviewItem } = useKnowledgeGraphDropdownItems();
    const { createNewScreenItem, addToCurrentScreenItem } =
      useScreenerDropdownItems();

    const {
      data: themes = [],
      isLoading: themesLoading,
      isRefetching: themesRefetching,
      error: themesError,
    } = useThemeExposure(entityTag, isEtf);

    const companyTags =
      themes.flatMap(theme => {
        if (theme.companies) {
          return theme.companies.map(item => item.tag);
        } else if (theme.funds) {
          return theme.funds.map(item => item.tag);
        } else {
          return [];
        }
      }) ?? [];

    const {
      data: entities = [],
      isLoading: entitiesLoading,
      isError: entitiesError,
    } = useEntitiesWithReactQuery(companyTags);

    const dropdownItems = (id: string, name: string) => {
      return [
        goToThemeOverviewItem(id, true),
        addToCurrentScreenItem({ name, id, key: 'theme_exposure' }),
        createNewScreenItem({ name, id, key: 'theme_exposure' }),
      ];
    };

    let themeContent =
      themes?.map(theme => ({
        title: capitalize(theme.name),
        id: theme.id,
        description: theme.evidence,
        companies: theme.companies
          ? theme.companies
              .map(company =>
                entities.find(entity => entity?.tag === company.tag)
              )
              .filter(Boolean)
          : [],
      })) ?? [];

    return (
      <div ref={ref}>
        <KnowledgeGraphModule
          data-testid="theme-exposure-knowledge"
          title={t('widget:assetOverview.themeExposure')}
          tooltip={t('widget:assetOverview:knowledgeGraphTooltip', {
            context: 'themeExposure',
          })}
          collapsedHeight={220}
          isLoading={themesLoading || entitiesLoading || themesRefetching}
          isError={
            (themesError && themesError.status !== httpCode.notFound) ||
            entitiesError
          }
          isEmpty={!themes.length || !entities.length}
        >
          {themeContent.map(theme => (
            <S.ThemeListItem key={theme.id} data-testid="theme-exposure-item">
              <S.StyledPillDropdown
                label={theme.title}
                dropdownItems={dropdownItems(theme.id, theme.title)}
              />
              <S.StyledListDescription>
                {theme.description}
              </S.StyledListDescription>
            </S.ThemeListItem>
          ))}
        </KnowledgeGraphModule>
      </div>
    );
  }
);
